/* @HEADER
 * @version 1.0.946 | 2026-09-01
 * @purpose Decides whether the project list can be shown yet, or whether ownership of at least one project is still genuinely unknown.
 * @sidecar projectListReadiness.ts.header.md
 * @layer app | @hex _none_ | @ctx workspace
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-5453, ZVX-DEV-5447, ZVX-DEV-5439, ZVX-DEV-5430, ZVX-DEV-5401

import type { ProjectRecord } from './projectStore.ts'

/**
 * ## Why this exists
 *
 * ZVX-DEV-5447 stopped listing a project provably belonging to another account,
 * and named the window it could not close: the ownership marks are written by
 * the registry sync, so between the app rendering and the first pull answering,
 * a project left by somebody else carries no mark and is listed. On a shared
 * machine those are a few seconds of exactly the leak this chain exists to
 * remove.
 *
 * The operator asked for a placeholder with a spinner and an explanation rather
 * than a blank screen.
 *
 * ## Why the condition is narrow
 *
 * A placeholder that shows too often is its own defect. Someone who has used
 * this machine for a year would meet a spinner at every launch, for a question
 * answered long ago. So the list waits only when ALL of these hold:
 *
 *   - somebody is signed in (signed out decides nothing — ZVX-DEV-5430);
 *   - the first registry sync of this session has not settled;
 *   - there is at least one project;
 *   - at least one of them carries NO ownership mark.
 *
 * The last keeps the common case instant: after one successful sync every
 * project is marked, and every later launch renders immediately.
 *
 * «Settled» means answered OR failed. A failed pull must not hold the app
 * hostage — ZVX-DEV-5401 established that «we could not ask» decides nothing,
 * and an endless spinner is a worse answer than an honest list.
 */

export type ProjectListReadiness =
  | { state: 'ready' }
  | { state: 'checking'; reason: 'ownership-unknown' }

export function resolveProjectListReadiness(input: {
  accountId: string | null | undefined
  /** Has the first registry sync of this session answered or failed? */
  firstSyncSettled: boolean
  projects: readonly ProjectRecord[]
}): ProjectListReadiness {
  const accountId =
    typeof input.accountId === 'string' && input.accountId.length > 0 ? input.accountId : null
  if (accountId === null) return { state: 'ready' }
  if (input.firstSyncSettled) return { state: 'ready' }

  const projects = Array.isArray(input.projects) ? input.projects : []
  const undecided = projects.some(
    (p) =>
      !(typeof p.ownerAccountId === 'string' && p.ownerAccountId.length > 0) &&
      !(typeof p.refusedForAccountId === 'string' && p.refusedForAccountId.length > 0),
  )
  return undecided ? { state: 'checking', reason: 'ownership-unknown' } : { state: 'ready' }
}
